import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { ListEditDeleteActions, ListHistoryActions } from '../../../components/admin/ListRowActions';
import './quote-generator.css';
import { apiRequest } from '../../../api/client';

interface SimpleQuote {
    id: string;
    serialNumber: number | null;
    clientName: string;
    jobTitle: string;
    total: number;
    status: string;
    date: string;
    email?: string;
}

export default function QuotesList(): React.JSX.Element {
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState<SimpleQuote[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [resendingId, setResendingId] = useState<string | null>(null);
    const formatStatus = (status: string) => status.charAt(0).toUpperCase() + status.slice(1);

    const openQuote = (id: string) => navigate(`/admin/quotes/new?id=${encodeURIComponent(id)}`);

    useEffect(() => {
        apiRequest<{ data: any[] }>('/api/quotes')
            .then((data) => {
                const mapped: SimpleQuote[] = data.data.map((q) => ({
                    id: q._id,
                    serialNumber: typeof q.serialNumber === 'number' ? q.serialNumber : null,
                    clientName: q.meta?.client?.firstName || q.meta?.client?.lastName
                        ? `${q.meta?.client?.firstName || ''} ${q.meta?.client?.lastName || ''}`.trim()
                        : q.contact?.name || 'New Client',
                    jobTitle: q.meta?.overview?.jobTitle || q.title || 'Untitled Quote',
                    total: q.total || 0,
                    status: q.status || 'draft',
                    date: q.updatedAt || q.createdAt,
                    email: (q.meta?.client?.email || q.contact?.email || '').trim() || undefined
                }));
                setErrorMessage(null);
                setQuotes(mapped);
            })
            .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : 'Failed to load quotes.';
                setErrorMessage(message);
                setQuotes([]);
            });
    }, []);

    const draftQuotes = quotes.filter((q) => q.status === 'draft');
    const sentQuotes = quotes.filter((q) => q.status !== 'draft');

    const handleDelete = (id: string) => {
        if (window.confirm('Are you sure you want to delete this quote? This action cannot be undone.')) {
            apiRequest(`/api/quotes/${id}`, { method: 'DELETE' })
                .then(() => {
                    setQuotes((prev) => prev.filter((q) => q.id !== id));
                    setSuccessMessage(null);
                })
                .catch((error: unknown) => {
                    const message = error instanceof Error ? error.message : 'Failed to delete quote.';
                    setErrorMessage(message);
                });
        }
    };

    const handleResend = (q: SimpleQuote) => {
        setErrorMessage(null);
        setSuccessMessage(null);
        if (!q.email) {
            if (
                window.confirm(
                    'This quote has no email on file. Open it to add an email and resend from the quote form?'
                )
            ) {
                openQuote(q.id);
            }
            return;
        }
        if (!window.confirm(`Resend this quote to ${q.email}?`)) return;

        setResendingId(q.id);
        apiRequest('/api/quotes/send', {
            method: 'POST',
            json: { quoteId: q.id, email: q.email }
        })
            .then(() => {
                setSuccessMessage(`Quote #${q.serialNumber ?? '—'} resent to ${q.email}.`);
                setQuotes((prev) =>
                    prev.map((row) =>
                        row.id === q.id ? { ...row, date: new Date().toISOString(), status: 'sent' } : row
                    )
                );
            })
            .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : 'Failed to resend quote.';
                setErrorMessage(message);
            })
            .finally(() => setResendingId(null));
    };

    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container" style={{ maxWidth: '1000px' }}>
                    {errorMessage && (
                        <div className="banner banner-error" style={{ marginBottom: '16px' }}>
                            {errorMessage}
                        </div>
                    )}
                    {successMessage && (
                        <div className="banner banner-success" style={{ marginBottom: '16px' }}>
                            {successMessage}
                        </div>
                    )}
                    <header className="quote-header" style={{ marginBottom: '32px' }}>
                        <div>
                            <h1 style={{ fontSize: '2.5rem', marginBottom: '8px' }}>Quotes</h1>
                            <p style={{ color: 'var(--dash-muted)', margin: 0, fontSize: '1.05rem' }}>
                                Manage your proposals, track statuses, and view history.
                            </p>
                        </div>
                        <button
                            className="btn-primary"
                            style={{
                                padding: '12px 24px',
                                fontSize: '0.95rem',
                                boxShadow: '0 4px 14px rgba(46, 138, 87, 0.3)'
                            }}
                            onClick={() => navigate('/admin/quotes/new')}
                        >
                            + Create New Quote
                        </button>
                    </header>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                        {draftQuotes.length > 0 && (
                            <section>
                                <div
                                    className="quote-section-title"
                                    style={{ border: 'none', fontSize: '1.25rem', marginBottom: '16px' }}
                                >
                                    Drafts ({draftQuotes.length})
                                </div>
                                <div className="quote-card" style={{ padding: 0, overflow: 'hidden' }}>
                                    <div className="list-responsive">
                                        <div className="list-table-pane quote-table-container">
                                            <table className="invoice-table">
                                                <thead>
                                                    <tr>
                                                        <th>Quote #</th>
                                                        <th>Last Edited</th>
                                                        <th>Client Info</th>
                                                        <th>Job Details</th>
                                                        <th style={{ textAlign: 'right' }}>Est. Total</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {draftQuotes.map((q) => (
                                                        <tr
                                                            key={q.id}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => openQuote(q.id)}
                                                        >
                                                            <td style={{ fontWeight: 600 }}>
                                                                #{q.serialNumber ?? '—'}
                                                            </td>
                                                            <td style={{ color: 'var(--dash-muted)' }}>
                                                                {new Date(q.date).toLocaleDateString()}
                                                            </td>
                                                            <td>
                                                                <div className="client-name">{q.clientName}</div>
                                                            </td>
                                                            <td>
                                                                <div style={{ fontWeight: 500 }}>{q.jobTitle}</div>
                                                                <div
                                                                    className="status-badge draft"
                                                                    style={{ marginTop: '6px' }}
                                                                >
                                                                    Draft
                                                                </div>
                                                            </td>
                                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                                                ${q.total.toFixed(2)}
                                                            </td>
                                                            <td style={{ textAlign: 'right' }}>
                                                                <ListEditDeleteActions
                                                                    onEdit={() => openQuote(q.id)}
                                                                    onDelete={() => handleDelete(q.id)}
                                                                    deleteTitle="Delete quote"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="list-cards-pane">
                                            {draftQuotes.map((q) => (
                                                <article
                                                    key={`m-${q.id}`}
                                                    className="list-land-card list-land-card--clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => openQuote(q.id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            openQuote(q.id);
                                                        }
                                                    }}
                                                >
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Quote #</span>
                                                        <span className="list-land-value">
                                                            #{q.serialNumber ?? '—'}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Est. total</span>
                                                        <span className="list-land-value">
                                                            ${q.total.toFixed(2)}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Client</span>
                                                        <span className="list-land-value">{q.clientName}</span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Job</span>
                                                        <span className="list-land-value">{q.jobTitle}</span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Last edited</span>
                                                        <span className="list-land-value list-land-value--muted">
                                                            {new Date(q.date).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Status</span>
                                                        <span className="list-land-value">
                                                            <span className="status-badge draft">Draft</span>
                                                        </span>
                                                    </div>
                                                    <div className="list-land-actions">
                                                        <ListEditDeleteActions
                                                            onEdit={() => openQuote(q.id)}
                                                            onDelete={() => handleDelete(q.id)}
                                                            deleteTitle="Delete quote"
                                                        />
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}

                        <section>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '16px'
                                }}
                            >
                                <div
                                    className="quote-section-title"
                                    style={{ border: 'none', fontSize: '1.25rem', margin: 0 }}
                                >
                                    Sent History
                                </div>
                                {sentQuotes.length > 0 && (
                                    <span style={{ fontSize: '0.9rem', color: 'var(--dash-muted)' }}>
                                        {sentQuotes.length} record(s)
                                    </span>
                                )}
                            </div>

                            <div className="quote-card" style={{ padding: 0, overflow: 'hidden', minHeight: '200px' }}>
                                {sentQuotes.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-state-icon">📭</div>
                                        <h3>No History Yet</h3>
                                        <p>Quotes you send to clients will appear here automatically.</p>
                                    </div>
                                ) : (
                                    <div className="list-responsive">
                                        <div className="list-table-pane quote-table-container">
                                            <table className="invoice-table">
                                                <thead>
                                                    <tr>
                                                        <th>Quote #</th>
                                                        <th>Sent Date</th>
                                                        <th>Client</th>
                                                        <th>Job Title</th>
                                                        <th>Sent To</th>
                                                        <th>Status</th>
                                                        <th style={{ textAlign: 'right' }}>Total</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sentQuotes.map((q) => (
                                                        <tr
                                                            key={q.id}
                                                            style={{
                                                                cursor: 'pointer',
                                                                opacity: resendingId === q.id ? 0.65 : 1
                                                            }}
                                                            onClick={() => openQuote(q.id)}
                                                        >
                                                            <td style={{ fontWeight: 600 }}>
                                                                #{q.serialNumber ?? '—'}
                                                            </td>
                                                            <td style={{ color: 'var(--dash-muted)' }}>
                                                                {new Date(q.date).toLocaleDateString()}
                                                            </td>
                                                            <td>
                                                                <div className="client-name">{q.clientName}</div>
                                                            </td>
                                                            <td>{q.jobTitle}</td>
                                                            <td style={{ color: 'var(--dash-muted)' }}>
                                                                {q.email || '—'}
                                                            </td>
                                                            <td>
                                                                <span className="status-badge sent">
                                                                    {formatStatus(q.status)}
                                                                </span>
                                                            </td>
                                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                                                ${q.total.toFixed(2)}
                                                            </td>
                                                            <td style={{ textAlign: 'right' }}>
                                                                <ListHistoryActions
                                                                    onOpen={() => openQuote(q.id)}
                                                                    onResend={() => handleResend(q)}
                                                                    onDelete={() => handleDelete(q.id)}
                                                                    openTitle="Open quote"
                                                                    resendTitle={
                                                                        resendingId === q.id
                                                                            ? 'Resending…'
                                                                            : 'Resend quote'
                                                                    }
                                                                    deleteTitle="Delete quote"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="list-cards-pane">
                                            {sentQuotes.map((q) => (
                                                <article
                                                    key={`m-${q.id}`}
                                                    className="list-land-card list-land-card--clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    style={{ opacity: resendingId === q.id ? 0.65 : 1 }}
                                                    onClick={() => openQuote(q.id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            openQuote(q.id);
                                                        }
                                                    }}
                                                >
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Quote #</span>
                                                        <span className="list-land-value">
                                                            #{q.serialNumber ?? '—'}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Total</span>
                                                        <span className="list-land-value">
                                                            ${q.total.toFixed(2)}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Client</span>
                                                        <span className="list-land-value">{q.clientName}</span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Job title</span>
                                                        <span className="list-land-value">{q.jobTitle}</span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Sent to</span>
                                                        <span className="list-land-value list-land-value--muted">
                                                            {q.email || '—'}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Sent</span>
                                                        <span className="list-land-value list-land-value--muted">
                                                            {new Date(q.date).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Status</span>
                                                        <span className="list-land-value">
                                                            <span className="status-badge sent">
                                                                {formatStatus(q.status)}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <div className="list-land-actions">
                                                        <ListHistoryActions
                                                            onOpen={() => openQuote(q.id)}
                                                            onResend={() => handleResend(q)}
                                                            onDelete={() => handleDelete(q.id)}
                                                            openTitle="Open quote"
                                                            resendTitle={
                                                                resendingId === q.id
                                                                    ? 'Resending…'
                                                                    : 'Resend quote'
                                                            }
                                                            deleteTitle="Delete quote"
                                                        />
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    <div style={{ marginTop: '40px', textAlign: 'center' }}>
                        <button
                            className="dash-btn dash-btn-ghost"
                            onClick={() => navigate('/admin/dashboard')}
                            style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}
                        >
                            ← Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
